// NatureMetadata.jsx
//
// Admin dashboard page for managing natural-location metadata.
// Mirrors the visual style of LocationSubmissions.js (slate/violet, Tailwind,
// Lucide icons, Spanish labels).
//
// Scope reminder: this page edits metadata on EXISTING locations. New
// locations are created via the LocationSubmissions approval flow, not here.
// The "Agregar Metadatos" button opens the dialog in add-mode for a location
// that doesn't have metadata yet.
//
// The same dialog is used for both add and edit — they end up calling the
// same upsertFullMeta() under the hood.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Search, MapPin, X, Loader2, Plus, Edit2, Save,
  RefreshCw, AlertTriangle, AlertCircle, CheckCircle,
  Database, Mountain, Waves, Droplets, TreePine, Aperture, Trash2,
  ChevronDown, Filter, Tag,
} from 'lucide-react';
import { useNatureMeta } from '../../authentication_components/natureMetaContext';

// =============================================================================
// ENUMS  (Spanish labels matching the backend's allowed values)
// =============================================================================

const ENUMS = {
  parking_availability: [
    { value: 'none', label: 'Ninguno' },
    { value: 'roadside', label: 'En la carretera' },
    { value: 'small_lot', label: 'Estacionamiento pequeño' },
    { value: 'large_lot', label: 'Estacionamiento grande' },
    { value: 'paid_lot', label: 'Estacionamiento de pago' },
  ],
  accessibility: [
    { value: 'wheelchair_accessible', label: 'Accesible silla de ruedas' },
    { value: 'limited', label: 'Acceso limitado' },
    { value: 'difficult', label: 'Acceso difícil' },
    { value: 'not_accessible', label: 'No accesible' },
  ],
  entrance_fee: [
    { value: 'free', label: 'Gratis' },
    { value: 'donation', label: 'Donación' },
    { value: 'paid', label: 'De pago' },
  ],
  crowd_level: [
    { value: 'low', label: 'Poca gente' },
    { value: 'moderate', label: 'Moderado' },
    { value: 'high', label: 'Concurrido' },
    { value: 'very_high', label: 'Muy concurrido' },
  ],
  best_time: [
    { value: 'morning', label: 'Mañana' },
    { value: 'afternoon', label: 'Tarde' },
    { value: 'evening', label: 'Atardecer' },
    { value: 'anytime', label: 'Cualquier momento' },
  ],
  safety_level: [
    { value: 'safe', label: 'Seguro' },
    { value: 'caution', label: 'Precaución' },
    { value: 'risky', label: 'Riesgoso' },
    { value: 'dangerous', label: 'Peligroso' },
  ],
  difficulty: [
    { value: 'easy', label: 'Fácil' },
    { value: 'moderate', label: 'Moderado' },
    { value: 'hard', label: 'Difícil' },
    { value: 'expert', label: 'Experto' },
  ],
  water_flow: [
    { value: 'calm', label: 'Calmado' },
    { value: 'moderate', label: 'Moderado' },
    { value: 'strong', label: 'Fuerte' },
    { value: 'variable', label: 'Variable' },
  ],
  swimming_allowed: [
    { value: 'yes', label: 'Sí' },
    { value: 'restricted', label: 'Restringido' },
    { value: 'seasonal', label: 'Por temporada' },
    { value: 'no', label: 'No' },
  ],
  depth_level: [
    { value: 'shallow', label: 'Poco profundo' },
    { value: 'moderate', label: 'Moderado' },
    { value: 'deep', label: 'Profundo' },
    { value: 'varied', label: 'Variado' },
  ],
  wave_intensity: [
    { value: 'calm', label: 'Calmado' },
    { value: 'light', label: 'Suave' },
    { value: 'moderate', label: 'Moderado' },
    { value: 'strong', label: 'Fuerte' },
    { value: 'dangerous', label: 'Peligroso' },
  ],
  sand_type: [
    { value: 'white', label: 'Arena blanca' },
    { value: 'golden', label: 'Arena dorada' },
    { value: 'brown', label: 'Arena marrón' },
    { value: 'black', label: 'Arena negra' },
    { value: 'rocky', label: 'Rocoso' },
    { value: 'coral', label: 'Coral' },
  ],
  swimming_safety: [
    { value: 'safe', label: 'Seguro' },
    { value: 'moderate', label: 'Moderado' },
    { value: 'dangerous', label: 'Peligroso' },
    { value: 'prohibited', label: 'Prohibido' },
  ],
  lifeguard: [
    { value: 'always', label: 'Siempre' },
    { value: 'weekends_only', label: 'Solo fines de semana' },
    { value: 'seasonal', label: 'Por temporada' },
    { value: 'none', label: 'Ninguno' },
  ],
  quality: [
    { value: 'poor', label: 'Pobre' },
    { value: 'fair', label: 'Regular' },
    { value: 'good', label: 'Bueno' },
    { value: 'excellent', label: 'Excelente' },
  ],
  surf_level: [
    { value: 'none', label: 'Ninguno' },
    { value: 'beginner', label: 'Principiante' },
    { value: 'intermediate', label: 'Intermedio' },
    { value: 'advanced', label: 'Avanzado' },
    { value: 'pro', label: 'Profesional' },
  ],
  swimming_at_base: [
    { value: 'allowed', label: 'Permitido' },
    { value: 'restricted', label: 'Restringido' },
    { value: 'dangerous', label: 'Peligroso' },
    { value: 'none', label: 'No disponible' },
  ],
  flow_seasonality: [
    { value: 'year_round', label: 'Todo el año' },
    { value: 'wet_season', label: 'Temporada de lluvia' },
    { value: 'variable', label: 'Variable' },
  ],
  trail_type: [
    { value: 'loop', label: 'Circuito' },
    { value: 'out_and_back', label: 'Ida y vuelta' },
    { value: 'point_to_point', label: 'Punto a punto' },
  ],
  terrain: [
    { value: 'paved', label: 'Pavimentado' },
    { value: 'dirt', label: 'Tierra' },
    { value: 'rocky', label: 'Rocoso' },
    { value: 'mixed', label: 'Mixto' },
  ],
  tour_requirement: [
    { value: 'none', label: 'No requerido' },
    { value: 'recommended', label: 'Recomendado' },
    { value: 'required', label: 'Requerido' },
    { value: 'not_available', label: 'No disponible' },
  ],
  lighting: [
    { value: 'natural', label: 'Natural' },
    { value: 'artificial', label: 'Artificial' },
    { value: 'mixed', label: 'Mixta' },
    { value: 'none', label: 'Ninguna' },
  ],
  equipment_required: [
    { value: 'none', label: 'Ninguno' },
    { value: 'basic', label: 'Básico' },
    { value: 'technical', label: 'Técnico' },
  ],
  bioluminescence: [
    { value: 'none', label: 'Ninguna' },
    { value: 'rare', label: 'Rara' },
    { value: 'moderate', label: 'Moderada' },
    { value: 'bright', label: 'Brillante' },
    { value: 'world_class', label: 'Clase mundial' },
  ],
  water_type: [
    { value: 'freshwater', label: 'Agua dulce' },
    { value: 'brackish', label: 'Salobre' },
    { value: 'saltwater', label: 'Agua salada' },
  ],
  wildlife_focus: [
    { value: 'birds', label: 'Aves' },
    { value: 'manatees', label: 'Manatíes' },
    { value: 'fish', label: 'Peces' },
    { value: 'turtles', label: 'Tortugas' },
    { value: 'crustaceans', label: 'Crustáceos' },
    { value: 'mixed', label: 'Mixta' },
    { value: 'none', label: 'Ninguna' },
  ],
};

// Map raw DB location_type → canonical type used in URLs and detail keys.
const TYPE_CANONICAL = {
  beach: 'beach',
  river: 'river',
  charco: 'river',
  waterfall: 'waterfall',
  mountain: 'mountain',
  pico: 'mountain',
  cavern: 'cavern',
  cave: 'cavern',
  laguna: 'lagoon',
  lagoon: 'lagoon',
};

// Maps canonical type to detail-block key in the FullMetaUpsertRequest body.
const DETAIL_KEY_FOR = {
  beach: 'beach_details',
  river: 'river_details',
  waterfall: 'waterfall_details',
  mountain: 'mountain_details',
  cavern: 'cavern_details',
  lagoon: 'lagoon_details',
};

const TYPE_BADGES = {
  beach: { label: 'Playa', color: 'sky', Icon: Waves },
  river: { label: 'Río/Charco', color: 'cyan', Icon: Droplets },
  waterfall: { label: 'Cascada', color: 'blue', Icon: Droplets },
  mountain: { label: 'Montaña', color: 'emerald', Icon: Mountain },
  cavern: { label: 'Cueva', color: 'slate', Icon: Aperture },
  lagoon: { label: 'Laguna', color: 'teal', Icon: Waves },
};

const ALL_TYPE_FILTERS = ['all', 'beach', 'river', 'waterfall', 'mountain', 'cavern', 'lagoon'];

// =============================================================================
// HELPERS
// =============================================================================

const toCanonical = (rawType) => TYPE_CANONICAL[(rawType || '').toLowerCase()] || null;

// Normalize form payload before sending to backend: empty strings → null,
// keep zero-numbers and false-booleans as-is.
const normalizePayload = (obj) => {
  if (!obj) return null;
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === '' || v === undefined) {
      out[k] = null;
    } else {
      out[k] = v;
    }
  });
  return out;
};

// =============================================================================
// SHARED FORM PRIMITIVES
// =============================================================================

const SelectField = ({ label, value, onChange, options, hint }) => (
  <div className="flex flex-col">
    <label className="text-xs font-bold text-slate-500 uppercase mb-1.5">{label}</label>
    <div className="relative">
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full appearance-none px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none pr-8"
      >
        <option value="">— sin establecer —</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <ChevronDown className="h-4 w-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
    {hint && <span className="text-[11px] text-slate-400 mt-1">{hint}</span>}
  </div>
);

const TextAreaField = ({ label, value, onChange, rows = 3, placeholder }) => (
  <div className="flex flex-col col-span-2">
    <label className="text-xs font-bold text-slate-500 uppercase mb-1.5">{label}</label>
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      rows={rows}
      placeholder={placeholder}
      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none resize-y"
    />
  </div>
);

const NumberField = ({ label, value, onChange, hint, step = 1, min = 0 }) => (
  <div className="flex flex-col">
    <label className="text-xs font-bold text-slate-500 uppercase mb-1.5">{label}</label>
    <input
      type="number"
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? null : (step < 1 ? parseFloat(v) : parseInt(v, 10)));
      }}
      step={step}
      min={min}
      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
    />
    {hint && <span className="text-[11px] text-slate-400 mt-1">{hint}</span>}
  </div>
);

const TriStateBoolField = ({ label, value, onChange }) => {
  // Tristate: null = unset, true = sí, false = no
  const buttons = [
    { val: true, label: 'Sí', cls: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
    { val: false, label: 'No', cls: 'bg-red-50 border-red-300 text-red-700' },
    { val: null, label: 'Sin establecer', cls: 'bg-slate-50 border-slate-300 text-slate-500' },
  ];
  return (
    <div className="flex flex-col">
      <label className="text-xs font-bold text-slate-500 uppercase mb-1.5">{label}</label>
      <div className="flex gap-1">
        {buttons.map((b) => {
          const active = value === b.val;
          return (
            <button
              key={String(b.val)}
              type="button"
              onClick={() => onChange(b.val)}
              className={`flex-1 px-2 py-2 text-xs font-medium rounded-lg border transition-all ${
                active ? `${b.cls} ring-2 ring-offset-1 ring-violet-500` : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {b.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// COMMON METADATA FORM
// =============================================================================

const CommonMetaForm = ({ value, onChange }) => {
  const setField = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SelectField label="Estacionamiento" value={value.parking_availability} onChange={setField('parking_availability')} options={ENUMS.parking_availability} />
      <SelectField label="Accesibilidad" value={value.accessibility} onChange={setField('accessibility')} options={ENUMS.accessibility} />
      <SelectField label="Costo de Entrada" value={value.entrance_fee} onChange={setField('entrance_fee')} options={ENUMS.entrance_fee} />
      <SelectField label="Nivel de Concurrencia" value={value.crowd_level} onChange={setField('crowd_level')} options={ENUMS.crowd_level} />
      <SelectField label="Mejor Hora" value={value.best_time} onChange={setField('best_time')} options={ENUMS.best_time} />
      <SelectField label="Nivel de Seguridad" value={value.safety_level} onChange={setField('safety_level')} options={ENUMS.safety_level} />
      <TextAreaField label="Descripción" value={value.description} onChange={setField('description')} rows={4} placeholder="Descripción pública del lugar..." />
    </div>
  );
};

// =============================================================================
// PER-TYPE DETAIL FORMS
// =============================================================================

const BeachDetailsForm = ({ value, onChange }) => {
  const setField = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SelectField label="Intensidad de Olas" value={value.wave_intensity} onChange={setField('wave_intensity')} options={ENUMS.wave_intensity} />
      <SelectField label="Tipo de Arena" value={value.sand_type} onChange={setField('sand_type')} options={ENUMS.sand_type} />
      <SelectField label="Seguridad para Nadar" value={value.swimming_safety} onChange={setField('swimming_safety')} options={ENUMS.swimming_safety} />
      <SelectField label="Salvavidas" value={value.lifeguard_presence} onChange={setField('lifeguard_presence')} options={ENUMS.lifeguard} />
      <SelectField label="Calidad del Snorkel" value={value.snorkeling_quality} onChange={setField('snorkeling_quality')} options={ENUMS.quality} />
      <SelectField label="Nivel de Surf" value={value.surf_level} onChange={setField('surf_level')} options={ENUMS.surf_level} />
      <TextAreaField label="Notas" value={value.notes} onChange={setField('notes')} placeholder="Información adicional..." />
    </div>
  );
};

const RiverDetailsForm = ({ value, onChange }) => {
  const setField = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SelectField label="Flujo del Agua" value={value.water_flow} onChange={setField('water_flow')} options={ENUMS.water_flow} />
      <SelectField label="Permite Nadar" value={value.swimming_allowed} onChange={setField('swimming_allowed')} options={ENUMS.swimming_allowed} />
      <SelectField label="Nivel de Profundidad" value={value.depth_level} onChange={setField('depth_level')} options={ENUMS.depth_level} />
      <SelectField label="Dificultad de Caminata" value={value.hike_difficulty} onChange={setField('hike_difficulty')} options={ENUMS.difficulty} />
      <TextAreaField label="Notas" value={value.notes} onChange={setField('notes')} placeholder="Información adicional..." />
    </div>
  );
};

const WaterfallDetailsForm = ({ value, onChange }) => {
  const setField = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <NumberField label="Altura (metros)" value={value.height_meters} onChange={setField('height_meters')} hint="Entero, positivo" />
      <SelectField label="Dificultad de Caminata" value={value.hike_difficulty} onChange={setField('hike_difficulty')} options={ENUMS.difficulty} />
      <SelectField label="Nadar en la Base" value={value.swimming_at_base} onChange={setField('swimming_at_base')} options={ENUMS.swimming_at_base} />
      <SelectField label="Estacionalidad del Flujo" value={value.flow_seasonality} onChange={setField('flow_seasonality')} options={ENUMS.flow_seasonality} />
      <TextAreaField label="Notas" value={value.notes} onChange={setField('notes')} placeholder="Información adicional..." />
    </div>
  );
};

const MountainDetailsForm = ({ value, onChange }) => {
  const setField = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <NumberField label="Elevación (metros)" value={value.elevation_meters} onChange={setField('elevation_meters')} hint="Entero, positivo" />
      <SelectField label="Dificultad" value={value.hike_difficulty} onChange={setField('hike_difficulty')} options={ENUMS.difficulty} />
      <NumberField label="Largo del Sendero (km)" value={value.trail_length_km} onChange={setField('trail_length_km')} step={0.1} hint="Decimal permitido" />
      <SelectField label="Tipo de Sendero" value={value.trail_type} onChange={setField('trail_type')} options={ENUMS.trail_type} />
      <SelectField label="Terreno" value={value.terrain} onChange={setField('terrain')} options={ENUMS.terrain} />
      <TextAreaField label="Notas" value={value.notes} onChange={setField('notes')} placeholder="Información adicional..." />
    </div>
  );
};

const CavernDetailsForm = ({ value, onChange }) => {
  const setField = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SelectField label="Dificultad" value={value.cave_difficulty} onChange={setField('cave_difficulty')} options={ENUMS.difficulty} />
      <SelectField label="Requiere Tour" value={value.tour_requirement} onChange={setField('tour_requirement')} options={ENUMS.tour_requirement} />
      <SelectField label="Iluminación" value={value.lighting} onChange={setField('lighting')} options={ENUMS.lighting} />
      <SelectField label="Equipo Requerido" value={value.equipment_required} onChange={setField('equipment_required')} options={ENUMS.equipment_required} />
      <TextAreaField label="Notas" value={value.notes} onChange={setField('notes')} placeholder="Información adicional..." />
    </div>
  );
};

const LagoonDetailsForm = ({ value, onChange }) => {
  const setField = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SelectField label="Bioluminiscencia" value={value.bioluminescence} onChange={setField('bioluminescence')} options={ENUMS.bioluminescence} />
      <SelectField label="Tipo de Agua" value={value.water_type} onChange={setField('water_type')} options={ENUMS.water_type} />
      <SelectField label="Permite Nadar" value={value.swimming_allowed} onChange={setField('swimming_allowed')} options={ENUMS.swimming_allowed} />
      <SelectField label="Tours en Bote" value={value.boat_tours} onChange={setField('boat_tours')} options={ENUMS.tour_requirement} />
      <SelectField label="Enfoque de Vida Silvestre" value={value.wildlife_focus} onChange={setField('wildlife_focus')} options={ENUMS.wildlife_focus} />
      <TriStateBoolField label="Kayak Disponible" value={value.kayaking_available} onChange={setField('kayaking_available')} />
      <TriStateBoolField label="Manglares Presentes" value={value.mangroves_present} onChange={setField('mangroves_present')} />
      <TriStateBoolField label="Bueno para Avistamiento" value={value.best_for_birding} onChange={setField('best_for_birding')} />
      <TextAreaField label="Notas" value={value.notes} onChange={setField('notes')} placeholder="Información adicional..." />
    </div>
  );
};

const DETAIL_FORM_FOR = {
  beach: BeachDetailsForm,
  river: RiverDetailsForm,
  waterfall: WaterfallDetailsForm,
  mountain: MountainDetailsForm,
  cavern: CavernDetailsForm,
  lagoon: LagoonDetailsForm,
};

// =============================================================================
// METADATA DIALOG  (handles both add and edit)
// =============================================================================

const MetadataDialog = ({ isOpen, mode, location, initialMeta, onClose, onSave, loading, allLocations }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [common, setCommon] = useState({});
  const [details, setDetails] = useState({});
  const [locationSearch, setLocationSearch] = useState('');

  // When the dialog opens, hydrate state from props.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedLocation(location || null);
    setCommon(initialMeta?.common || {});
    setLocationSearch('');
    if (initialMeta && location) {
      const canonical = toCanonical(location.location_type);
      const detailKey = DETAIL_KEY_FOR[canonical];
      setDetails(initialMeta[detailKey] || {});
    } else {
      setDetails({});
    }
  }, [isOpen, location, initialMeta]);

  // Filter locations for the picker (only shown in add mode).
  // IMPORTANT: this useMemo must run on EVERY render, including when isOpen
  // is false — hooks can't sit below an early return per the Rules of Hooks.
  const pickerOptions = useMemo(() => {
    if (mode !== 'add' || !allLocations) return [];
    const q = locationSearch.toLowerCase();
    return allLocations
      .filter((l) => toCanonical(l.location_type)) // skip types we don't support
      .filter((l) =>
        !q ||
        l.location_name?.toLowerCase().includes(q) ||
        l.location_city?.toLowerCase().includes(q) ||
        l.location_id?.toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [mode, allLocations, locationSearch]);

  if (!isOpen) return null;

  const canonical = selectedLocation ? toCanonical(selectedLocation.location_type) : null;
  const DetailForm = canonical ? DETAIL_FORM_FOR[canonical] : null;
  const typeBadge = canonical ? TYPE_BADGES[canonical] : null;

  const handleSave = () => {
    if (!selectedLocation) return;
    const detailKey = DETAIL_KEY_FOR[canonical];
    const payload = {
      common: normalizePayload(common),
      [detailKey]: normalizePayload(details),
    };
    onSave(selectedLocation, payload);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between bg-white">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              {mode === 'add' ? <Plus className="h-5 w-5 text-violet-600" /> : <Edit2 className="h-5 w-5 text-violet-600" />}
              {mode === 'add' ? 'Agregar Metadatos' : 'Editar Metadatos'}
            </h3>
            {selectedLocation && (
              <div className="mt-1 flex items-center gap-2 text-sm">
                <span className="font-semibold text-slate-700">{selectedLocation.location_name}</span>
                <span className="text-slate-400">•</span>
                <span className="text-slate-500 inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {selectedLocation.location_city || 'Sin ciudad'}
                </span>
                {typeBadge && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${typeBadge.color}-50 text-${typeBadge.color}-700 border border-${typeBadge.color}-200`}>
                      <typeBadge.Icon className="h-3 w-3" /> {typeBadge.label}
                    </span>
                  </>
                )}
                <span className="text-slate-400">•</span>
                <span className="text-xs font-mono text-slate-400">{selectedLocation.location_id}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50/40">

          {/* Add-mode: location picker */}
          {mode === 'add' && !selectedLocation && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">1. Seleccionar Ubicación</h4>
              <p className="text-sm text-slate-600 mb-3">
                Escoja una ubicación existente para añadir o editar sus metadatos.
              </p>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  placeholder="Buscar por nombre, ciudad, o ID..."
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                />
              </div>
              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {pickerOptions.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-400">Sin resultados</div>
                ) : pickerOptions.map((loc) => {
                  const locCanon = toCanonical(loc.location_type);
                  const badge = TYPE_BADGES[locCanon];
                  return (
                    <button
                      key={loc.location_id}
                      onClick={() => setSelectedLocation(loc)}
                      className="w-full px-4 py-3 text-left hover:bg-violet-50/40 transition-colors flex items-center justify-between"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 text-sm truncate">{loc.location_name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          <MapPin className="h-3 w-3" /> {loc.location_city || '—'}
                          <span className="text-slate-300">•</span>
                          <span className="font-mono">{loc.location_id}</span>
                        </div>
                      </div>
                      {badge && (
                        <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-50 text-${badge.color}-700 border border-${badge.color}-200 flex-shrink-0`}>
                          <badge.Icon className="h-3 w-3" /> {badge.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Common metadata section */}
          {selectedLocation && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-bold text-slate-800">Información General</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Aplica a todos los tipos de ubicación.</p>
                </div>
                <Tag className="h-4 w-4 text-slate-400" />
              </div>
              <CommonMetaForm value={common} onChange={setCommon} />
            </div>
          )}

          {/* Type-specific details section */}
          {selectedLocation && DetailForm && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    {typeBadge && <typeBadge.Icon className="h-4 w-4 text-slate-500" />}
                    Detalles de {typeBadge?.label || 'Ubicación'}
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">Campos específicos para este tipo.</p>
                </div>
              </div>
              <DetailForm value={details} onChange={setDetails} />
            </div>
          )}

          {selectedLocation && !DetailForm && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Sin detalles específicos disponibles</p>
                <p className="text-xs text-amber-700 mt-1">
                  El tipo "<span className="font-mono">{selectedLocation.location_type}</span>" no tiene
                  tabla de detalles asociada. Solo se pueden editar los datos generales.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center justify-between">
          <span className="text-xs text-slate-500">
            PUT reemplaza todos los campos. Campos vacíos se guardan como NULL.
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !selectedLocation}
              className="px-5 py-2 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 shadow-md shadow-violet-200 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" /> Guardar Metadatos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// SUCCESS TOAST  (matches LocationSubmissions pattern)
// =============================================================================

const SuccessToast = ({ message, onDismiss }) => {
  useEffect(() => {
    if (message) {
      const t = setTimeout(onDismiss, 3000);
      return () => clearTimeout(t);
    }
  }, [message, onDismiss]);

  if (!message) return null;
  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 shadow-lg">
        <CheckCircle className="h-5 w-5 text-emerald-600" />
        <p className="text-emerald-800 font-medium text-sm">{message}</p>
        <button onClick={onDismiss} className="text-emerald-400 hover:text-emerald-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const ErrorAlert = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-bold text-red-800 text-sm">Error</h4>
          <p className="text-red-600 text-sm mt-1">{message}</p>
        </div>
        <button onClick={onDismiss} className="text-red-400 hover:text-red-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function NatureMetadata() {
  const {
    loading: contextLoading,
    error: contextError,
    fetchLocations,
    getFullMeta,
    upsertFullMeta,
    clearError,
  } = useNatureMeta();

  // List state
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [completionFilter, setCompletionFilter] = useState('all'); // all | with_meta | without_meta

  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    mode: 'add', // 'add' | 'edit'
    location: null,
    initialMeta: null,
  });
  const [dialogLoading, setDialogLoading] = useState(false);

  // Toast/error
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState('');

  // Load locations on mount + when filters change
  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, completionFilter]);

  const loadLocations = async () => {
    try {
      setLocalError('');
      const filters = { limit: 500 };
      if (typeFilter !== 'all') filters.type = typeFilter;
      if (completionFilter !== 'all') {
        filters.has_meta = completionFilter === 'with_meta';
      }
      const result = await fetchLocations(filters);
      setLocations(result?.locations || []);
    } catch (err) {
      console.error('[NatureMetadata] Error fetching locations:', err);
      setLocalError(err.message || 'Error al cargar ubicaciones');
    }
  };

  // Local search filter
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return locations.filter((l) =>
      !q ||
      l.location_name?.toLowerCase().includes(q) ||
      l.location_city?.toLowerCase().includes(q) ||
      l.location_id?.toLowerCase().includes(q)
    );
  }, [locations, searchTerm]);

  // Open dialog for adding
  const handleOpenAdd = () => {
    setDialog({ isOpen: true, mode: 'add', location: null, initialMeta: null });
  };

  // Open dialog for editing — pre-loads existing meta
  const handleOpenEdit = async (location) => {
    setDialogLoading(true);
    setLocalError('');
    try {
      const meta = await getFullMeta(location.location_id);
      setDialog({
        isOpen: true,
        mode: 'edit',
        location,
        initialMeta: meta || { common: {} },
      });
    } catch (err) {
      console.error('[NatureMetadata] Error loading meta for edit:', err);
      setLocalError(err.message || 'Error al cargar metadatos');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialog({ isOpen: false, mode: 'add', location: null, initialMeta: null });
  };

  const handleSave = async (location, payload) => {
    setDialogLoading(true);
    setLocalError('');
    try {
      await upsertFullMeta(location.location_id, payload);
      setSuccessMessage(`Metadatos guardados para "${location.location_name}".`);
      handleCloseDialog();
      await loadLocations(); // refresh list to update has_meta indicators
    } catch (err) {
      console.error('[NatureMetadata] Error saving meta:', err);
      setLocalError(err.message || 'Error al guardar metadatos');
    } finally {
      setDialogLoading(false);
    }
  };

  const displayError = localError || contextError;

  // Per-row meta indicators — backend should return has_common_meta and
  // has_details on the list items. If your endpoint doesn't yet, the
  // indicators just won't light up; nothing breaks.
  const renderMetaIndicators = (loc) => {
    const hasCommon = loc.has_common_meta || (loc.parking_availability != null);
    const hasDetails = loc.has_details;
    return (
      <div className="flex items-center gap-1.5">
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
            hasCommon ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'
          }`}
          title="Información general"
        >
          GEN
        </span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
            hasDetails ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-slate-50 text-slate-400 border-slate-200'
          }`}
          title="Detalles específicos"
        >
          DET
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <SuccessToast message={successMessage} onDismiss={() => setSuccessMessage('')} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Metadatos de Ubicaciones Naturales</h1>
              <p className="text-sm text-slate-500">Editar información general y detalles específicos por tipo.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadLocations}
                disabled={contextLoading}
                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
                title="Actualizar"
              >
                <RefreshCw className={`h-5 w-5 ${contextLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleOpenAdd}
                className="px-4 py-2 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 shadow-md shadow-violet-200 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" /> Agregar Metadatos
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, ciudad o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
              <Filter className="h-4 w-4" /> Tipo:
            </div>
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
              {ALL_TYPE_FILTERS.map((t) => {
                const badge = TYPE_BADGES[t];
                const label = t === 'all' ? 'Todos' : badge?.label || t;
                return (
                  <button
                    key={t}
                    onClick={() => setTypeFilter(t)}
                    disabled={contextLoading}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap disabled:opacity-50 ${
                      typeFilter === t ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500 font-medium ml-2">
              Completitud:
            </div>
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              {[
                { key: 'all', label: 'Todos' },
                { key: 'with_meta', label: 'Con datos' },
                { key: 'without_meta', label: 'Sin datos' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setCompletionFilter(opt.key)}
                  disabled={contextLoading}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap disabled:opacity-50 ${
                    completionFilter === opt.key ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ErrorAlert message={displayError} onDismiss={() => { clearError(); setLocalError(''); }} />

      {/* Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
          {contextLoading && locations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-violet-500 animate-spin mb-2" />
              <p className="text-slate-400 text-sm">Cargando ubicaciones...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <div className="bg-slate-50 p-3 rounded-full mb-3">
                <MapPin className="h-6 w-6 text-slate-300" />
              </div>
              <p className="text-slate-500 font-medium">
                {searchTerm ? 'No se encontraron resultados.' : 'No hay ubicaciones en esta categoría.'}
              </p>
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="mt-2 text-sm text-violet-600 hover:text-violet-700">
                  Limpiar búsqueda
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Ubicación</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Ciudad</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Metadatos</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((loc) => {
                    const canonical = toCanonical(loc.location_type);
                    const badge = TYPE_BADGES[canonical];
                    return (
                      <tr
                        key={loc.location_id}
                        onClick={() => handleOpenEdit(loc)}
                        className="group hover:bg-violet-50/50 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-800">{loc.location_name}</div>
                          <div className="text-xs font-mono text-slate-400 mt-0.5">{loc.location_id}</div>
                        </td>
                        <td className="px-6 py-4">
                          {badge ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-50 text-${badge.color}-700 border border-${badge.color}-200`}>
                              <badge.Icon className="h-3 w-3" /> {badge.label}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">{loc.location_type || 'sin tipo'}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-slate-400" />
                            {loc.location_city || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {renderMetaIndicators(loc)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenEdit(loc); }}
                            disabled={dialogLoading}
                            className="p-2 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-100 transition-colors disabled:opacity-50"
                            title="Editar metadatos"
                          >
                            <Edit2 className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary */}
        {!contextLoading && filtered.length > 0 && (
          <div className="mt-4 text-center text-sm text-slate-500">
            Mostrando {filtered.length} de {locations.length} ubicaciones
            {searchTerm && ` (filtradas por "${searchTerm}")`}
          </div>
        )}
      </div>

      {/* Dialog */}
      <MetadataDialog
        isOpen={dialog.isOpen}
        mode={dialog.mode}
        location={dialog.location}
        initialMeta={dialog.initialMeta}
        allLocations={locations}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />
    </div>
  );
}